import { useCallback, useEffect, useState } from 'react';
import { RefreshCw, Settings, Trash2, Database, HardDrive } from 'lucide-react';
import { Skeleton } from '../../components/ui/Skeleton';
import { apiClient } from '../../lib/api-client';
import { CacheConfigDialog } from '../../components/admin/CacheConfigDialog';
import { useActivityHeartbeat } from '../../hooks/useActivityHeartbeat';
import { InlineConfirmation } from '../../components/ui/InlineConfirmation';
import { usePermission } from '../../hooks/usePermission';
import { PageHeader } from '../../components/ui/PageHeader';
import { Button } from '../../components/ui/Button';
import { useCacheEvents } from '../../hooks/useCacheEvents';

interface CacheInfo {
  name: string;
  description: string;
  defaultTtl: number;
  activeTtl: number;
  relatedPages: string[];
  enabled: boolean;
  connectTimeoutSeconds?: number;
  callTimeoutSeconds?: number;
  inactiveRefreshMode?: 'never' | 'daily' | 'interval';
  inactiveRefreshDailyTimes?: string[];
  inactiveRefreshIntervalSeconds?: number | null;
  activeRefreshMode?: 'never' | 'interval';
  activeRefreshIntervalSeconds?: number | null;
  keyCount: number;
  hits: number;
  misses: number;
  refreshes: number;
  hitRate: string;
  lastRefresh: string | null;
}

export function CacheManagement() {
  const [caches, setCaches] = useState<CacheInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState<string | null>(null);
  const [editingCache, setEditingCache] = useState<CacheInfo | null>(null);
  const [confirmClearCache, setConfirmClearCache] = useState<string | null>(null);
  const [confirmClearAll, setConfirmClearAll] = useState(false);

  // Permission checks
  const { isAllowed: canClearCache } = usePermission('cache_management', 'clear_cache');
  const { isAllowed: canDeleteCache } = usePermission('cache_management', 'delete');
  const { isAllowed: canManageCache } = usePermission('cache_management', 'manage_cache');

  // Send activity heartbeat
  useActivityHeartbeat('cache_management');

  useEffect(() => {
    void loadCaches();
  }, []);

  const loadCaches = useCallback(async () => {
    try {
      const response = await apiClient.get<{ caches: CacheInfo[] }>('/api/admin/cache/list');
      setCaches(response.caches);
    } catch (error: any) {
      console.error('Failed to load caches:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Refresh only when backend reports a cache version change.
  // (Admin view should not poll.)
  useCacheEvents({
    enabled: true,
    onEvent: () => {
      void loadCaches();
    },
  });

  const handleRefresh = async (cacheName: string) => {
    setRefreshing(cacheName);
    try {
      await apiClient.post(`/api/admin/cache/${cacheName}/refresh`);
      await loadCaches();
    } catch (error: any) {
      console.error('Failed to refresh cache:', error);
      alert(`Failed to refresh cache: ${error.message}`);
    } finally {
      setRefreshing(null);
    }
  };

  const handleClear = async (cacheName: string) => {
    try {
      await apiClient.delete(`/api/admin/cache/${cacheName}`);
      await loadCaches();
    } catch (error: any) {
      console.error('Failed to clear cache:', error);
      alert(`Failed to clear cache: ${error.message}`);
    } finally {
      setConfirmClearCache(null);
    }
  };

  const handleClearAll = async () => {
    try {
      await apiClient.delete('/api/admin/cache');
      await loadCaches();
    } catch (error: any) {
      console.error('Failed to clear all caches:', error);
      alert(`Failed to clear all caches: ${error.message}`);
    } finally {
      setConfirmClearAll(false);
    }
  };

  const formatTtl = (seconds: number): string => {
    if (seconds === 0) return '∞';
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
    return `${Math.floor(seconds / 3600)}h`;
  };

  const formatLastRefresh = (isoDate: string | null): string => {
    if (!isoDate) return 'Never';
    const date = new Date(isoDate);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins === 1) return '1 min ago';
    if (diffMins < 60) return `${diffMins} mins ago`;
    
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours === 1) return '1 hour ago';
    if (diffHours < 24) return `${diffHours} hours ago`;
    
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays === 1) return '1 day ago';
    return `${diffDays} days ago`;
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-4 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-32 w-full" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <PageHeader
        icon={HardDrive}
        iconColor="text-cyan-600 dark:text-cyan-400"
        iconBgColor="bg-cyan-100 dark:bg-cyan-900/30"
        description="Monitor and manage Redis caches with adaptive TTL"
        count={caches.length}
        countLabel="caches"
        countColor="text-cyan-600 dark:text-cyan-400"
        actions={
          (canClearCache || canManageCache) ? (
            <div className="w-full sm:w-auto sm:h-full flex items-stretch">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setConfirmClearCache(null);
                  setConfirmClearAll(true);
                }}
                className="w-full h-full text-sm py-2 text-red-600 border-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Clear All Caches
              </Button>
            </div>
          ) : undefined
        }
      />

      {(confirmClearAll || confirmClearCache) && (
        <InlineConfirmation
          message={
            confirmClearAll
              ? 'Clear ALL caches? This cannot be undone.'
              : `Clear cache "${confirmClearCache}"?`
          }
          confirmText={confirmClearAll ? 'Clear All' : 'Clear'}
          onConfirm={() => (confirmClearAll ? handleClearAll() : confirmClearCache ? handleClear(confirmClearCache) : undefined)}
          onCancel={() => {
            setConfirmClearAll(false);
            setConfirmClearCache(null);
          }}
        />
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Caches</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{caches.length}</p>
            </div>
            <Database className="w-8 h-8 text-blue-500" />
          </div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Keys</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {caches.reduce((sum, c) => sum + c.keyCount, 0)}
              </p>
            </div>
            <Database className="w-8 h-8 text-green-500" />
          </div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Cache Hits</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {caches.reduce((sum, c) => sum + c.hits, 0).toLocaleString()}
              </p>
            </div>
            <Database className="w-8 h-8 text-purple-500" />
          </div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Avg Hit Rate</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {caches.length > 0
                  ? (caches.reduce((sum, c) => sum + parseFloat(c.hitRate), 0) / caches.length).toFixed(1)
                  : '0.0'}%
              </p>
            </div>
            <Database className="w-8 h-8 text-orange-500" />
          </div>
        </div>
      </div>

      {/* Cache Table - Desktop */}
      <div className="hidden md:block bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-900">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Cache Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                TTL (Default / Active)
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Keys
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Hit Rate
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Stats
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Last Refresh
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Status
              </th>
              {(canClearCache || canManageCache || canDeleteCache) && (
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {caches.map((cache) => (
              <tr key={cache.name} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div>
                    <div className="text-sm font-medium text-gray-900 dark:text-white">{cache.name}</div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">{cache.description}</div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                  <div className="space-y-1">
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Default:</span> {formatTtl(cache.defaultTtl)}
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Active:</span> {formatTtl(cache.activeTtl)}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                  {cache.keyCount}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                      <div
                        className="bg-blue-500 h-2 rounded-full"
                        style={{ width: cache.hitRate }}
                      ></div>
                    </div>
                    <span className="text-sm text-gray-900 dark:text-white">{cache.hitRate}</span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  <div className="space-y-1">
                    <div>Hits: {cache.hits.toLocaleString()}</div>
                    <div>Misses: {cache.misses.toLocaleString()}</div>
                    <div>Refreshes: {cache.refreshes.toLocaleString()}</div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  {formatLastRefresh(cache.lastRefresh)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      cache.enabled
                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                        : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                    }`}
                  >
                    {cache.enabled ? 'Enabled' : 'Disabled'}
                  </span>
                </td>
                {(canClearCache || canManageCache || canDeleteCache) && (
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                    {canClearCache && (
                      <button
                        onClick={() => handleRefresh(cache.name)}
                        disabled={refreshing === cache.name}
                        className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 disabled:opacity-50"
                        title="Refresh cache"
                      >
                        <RefreshCw className={`w-4 h-4 ${refreshing === cache.name ? 'animate-spin' : ''}`} />
                      </button>
                    )}
                    {canManageCache && (
                      <button
                        onClick={() => setEditingCache(cache)}
                        className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-300"
                        title="Edit cache config"
                      >
                        <Settings className="w-4 h-4" />
                      </button>
                    )}
                    {(canDeleteCache || canClearCache) && (
                      <button
                        onClick={() => {
                          setConfirmClearAll(false);
                          setConfirmClearCache(cache.name);
                        }}
                        className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                        title="Clear cache"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Cache Cards - Mobile */}
      <div className="md:hidden space-y-4">
        {caches.map((cache) => (
          <div key={cache.name} className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 space-y-3">
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <h3 className="text-base font-medium text-gray-900 dark:text-white truncate">{cache.name}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{cache.description}</p>
              </div>
              <span className={`ml-2 px-2 py-1 text-xs font-medium rounded-full flex-shrink-0 ${
                cache.enabled
                  ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                  : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-400'
              }`}>
                {cache.enabled ? 'Enabled' : 'Disabled'}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-gray-500 dark:text-gray-400">Keys:</span>
                <span className="ml-1 font-medium text-gray-900 dark:text-white">{cache.keyCount}</span>
              </div>
              <div>
                <span className="text-gray-500 dark:text-gray-400">Hit Rate:</span>
                <span className={`ml-1 font-medium ${
                  parseFloat(cache.hitRate) >= 80 ? 'text-green-600 dark:text-green-400' :
                  parseFloat(cache.hitRate) >= 50 ? 'text-yellow-600 dark:text-yellow-400' :
                  'text-red-600 dark:text-red-400'
                }`}>
                  {cache.hitRate}%
                </span>
              </div>
              <div>
                <span className="text-gray-500 dark:text-gray-400">Hits:</span>
                <span className="ml-1 font-medium text-gray-900 dark:text-white">{cache.hits.toLocaleString()}</span>
              </div>
              <div>
                <span className="text-gray-500 dark:text-gray-400">Misses:</span>
                <span className="ml-1 font-medium text-gray-900 dark:text-white">{cache.misses.toLocaleString()}</span>
              </div>
              <div>
                <span className="text-gray-500 dark:text-gray-400">TTL:</span>
                <span className="ml-1 font-medium text-gray-900 dark:text-white">
                  {formatTtl(cache.defaultTtl)} / {formatTtl(cache.activeTtl)}
                </span>
              </div>
              <div>
                <span className="text-gray-500 dark:text-gray-400">Last:</span>
                <span className="ml-1 font-medium text-gray-900 dark:text-white">{formatLastRefresh(cache.lastRefresh)}</span>
              </div>
            </div>

            {confirmClearCache === cache.name && (
              <InlineConfirmation
                message={`Clear cache "${cache.name}"?`}
                confirmText="Clear"
                onConfirm={() => handleClear(cache.name)}
                onCancel={() => setConfirmClearCache(null)}
              />
            )}

            {(canClearCache || canManageCache || canDeleteCache) && (
              <div className="flex gap-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                {canClearCache && (
                  <button
                    onClick={() => handleRefresh(cache.name)}
                    disabled={refreshing === cache.name}
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    <RefreshCw className={`w-4 h-4 ${refreshing === cache.name ? 'animate-spin' : ''}`} />
                    Refresh
                  </button>
                )}
                {canManageCache && (
                  <button
                    onClick={() => setEditingCache(cache)}
                    className="flex items-center justify-center px-3 py-2 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600"
                  >
                    <Settings className="w-4 h-4" />
                  </button>
                )}
                {(canDeleteCache || canClearCache) && (
                  <button
                    onClick={() => {
                      setConfirmClearAll(false);
                      setConfirmClearCache(cache.name);
                    }}
                    className="flex items-center justify-center px-3 py-2 text-sm bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-md hover:bg-red-200 dark:hover:bg-red-900/50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Edit Dialog */}
      {editingCache && (
        <CacheConfigDialog
          cache={editingCache}
          onClose={() => setEditingCache(null)}
          onSave={async (updates) => {
            try {
              await apiClient.patch(`/api/admin/cache/${editingCache.name}/config`, updates);
              setEditingCache(null);
              await loadCaches();
            } catch (error: any) {
              console.error('Failed to update cache config:', error);
              alert(`Failed to update cache: ${error.message}`);
            }
          }}
        />
      )}
    </div>
  );
}

